import type { ErrorEnvelope } from '@caremesh/contracts';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly retryable: boolean;
  readonly fieldErrors?: unknown;

  constructor(envelope: ErrorEnvelope & { fieldErrors?: unknown }, status: number) {
    super(envelope.message);
    this.name = 'ApiError';
    this.code = envelope.code;
    this.status = status;
    this.retryable = envelope.retryable;
    if (envelope.fieldErrors) this.fieldErrors = envelope.fieldErrors;
  }
}

/**
 * Cliente do painel. O cookie de sessão é HttpOnly (o servidor seta no login),
 * então o cliente não guarda token em memória nem em storage acessível por JS.
 */
async function request<T>(input: {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
}): Promise<T> {
  const response = await fetch(input.path, {
    method: input.method,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(input.body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(input.body !== undefined ? { body: JSON.stringify(input.body) } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { code: 'INTERNAL_ERROR', message: 'Resposta inválida do servidor.', retryable: true };
    }
  }

  if (!response.ok) {
    const envelope = (parsed ?? {
      code: 'INTERNAL_ERROR',
      message: 'Falha na requisição.',
      retryable: true,
    }) as ErrorEnvelope;
    throw new ApiError(envelope, response.status);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>({ method: 'GET', path }),
  post: <T>(path: string, body?: unknown) => request<T>({ method: 'POST', path, body }),
  patch: <T>(path: string, body?: unknown) => request<T>({ method: 'PATCH', path, body }),
  del: <T>(path: string, body?: unknown) => request<T>({ method: 'DELETE', path, body }),
};
